import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AnimatedEllipsis } from '@/components/ui/animated-ellipsis';
import { Button, type ButtonProps } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export type EmptyStateAction = {
  label: string;
  onClick?: () => void;
  to?: string;
  icon?: ReactElement;
  variant?: ButtonProps['variant'];
  disabled?: boolean;
};

export type EmptyStateProps = {
  icon?: ReactElement | undefined;
  title: string;
  body?: string | undefined;
  actions?: EmptyStateAction[] | undefined;
  className?: string | undefined;
  // loading flips the container into "fetching" mode: same shell, animated
  // ellipsis appended to the title, body + actions suppressed. Lets a page
  // render the empty-state container as soon as the route mounts and swap
  // to data (or the real empty copy) without a layout jump.
  loading?: boolean;
};

export function EmptyState({
  icon,
  title,
  body,
  actions,
  className,
  loading,
}: EmptyStateProps) {
  const visibleActions = loading ? [] : actions?.filter(Boolean) ?? [];
  return (
    <section
      className={cn(
        'flex flex-col items-center gap-3 rounded-2xl border border-lime bg-[var(--color-accent-soft)] p-8 text-center sm:p-10',
        className,
      )}
      aria-busy={loading ? true : undefined}
    >
      {icon && (
        <span className="text-foreground/70 [&_svg]:h-10 [&_svg]:w-10">
          {icon}
        </span>
      )}
      <h2 className="font-heading text-xl font-bold">
        {loading ? stripTrailingEllipsis(title) : title}
        {loading && <AnimatedEllipsis />}
      </h2>
      {!loading && body && (
        <p className="max-w-md text-sm text-foreground/80">{body}</p>
      )}
      {visibleActions.length > 0 && (
        <div className="flex flex-wrap items-center justify-center gap-2">
          {visibleActions.map((action) => (
            <EmptyStateActionButton key={action.label} action={action} />
          ))}
        </div>
      )}
    </section>
  );
}

// Existing `*.loading` i18n keys all end with a "…" (or "...") character
// so they read naturally as plain text. When the AnimatedEllipsis is
// rendered alongside, that static tail would double up — trim it so the
// dots come exclusively from the animation.
function stripTrailingEllipsis(s: string): string {
  return s.replace(/(?:…|\.{3})\s*$/, '').trimEnd();
}

function EmptyStateActionButton({ action }: { action: EmptyStateAction }) {
  const content: ReactNode = (
    <>
      {action.icon}
      {action.label}
    </>
  );
  if (action.to) {
    return (
      <Button asChild variant={action.variant ?? 'default'}>
        <Link to={action.to}>{content}</Link>
      </Button>
    );
  }
  return (
    <Button
      type="button"
      onClick={action.onClick}
      disabled={action.disabled}
      variant={action.variant ?? 'default'}
    >
      {content}
    </Button>
  );
}
